import React, { useCallback, useRef, useState } from 'react';
import {
  TradingScoreWidgetInstanceConfig,
  TradingScoreDataContent,
  TradingScoreValue,
  TRADING_SCORE_OPTIONS,
  TRADING_SCORE_STYLES,
  TRADING_SCORE_LABELS,
} from './types';
import { WidgetEmptyState } from '../../../design';

interface TradingScoreWidgetRendererProps {
  config: TradingScoreWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: TradingScoreDataContent) => void;
  readOnly?: boolean;
}

const TradingScoreWidgetRenderer: React.FC<TradingScoreWidgetRendererProps> = ({
  config,
  dashboardData = {},
  onDataChange,
  readOnly = false,
}) => {
  const data = dashboardData[config.dataKey] as TradingScoreDataContent | undefined;
  const [localAction, setLocalAction] = useState<TradingScoreValue | undefined>(data?.action);
  const lastPropAction = useRef(data?.action);

  if (data?.action !== lastPropAction.current) {
    lastPropAction.current = data?.action;
    setLocalAction(data?.action);
  }

  const handleSelect = useCallback(
    (value: TradingScoreValue) => {
      if (readOnly || !onDataChange) return;
      setLocalAction(value);
      onDataChange(config.dataKey, { action: value });
    },
    [config.dataKey, onDataChange, readOnly],
  );

  if (!config.dataKey) {
    return (
      <WidgetEmptyState
        title="No data key configured"
        description="Configure a data key in the widget settings."
      />
    );
  }

  const showRequired = !localAction && !readOnly;
  // Match active state by display label so legacy long/short values
  // (e.g. sell_to_short) still highlight their collapsed button (Sell).
  const activeLabel = localAction ? TRADING_SCORE_LABELS[localAction] : undefined;

  return (
    <div className="h-full bg-surface p-3 sm:p-4 flex flex-col gap-2">
      <div className="w-full flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2 auto-rows-fr">
        {TRADING_SCORE_OPTIONS.map((option) => {
          const isActive = activeLabel === option.label;
          const styles = TRADING_SCORE_STYLES[option.value];

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              disabled={readOnly}
              aria-pressed={isActive}
              className={[
                'min-h-[56px] rounded-lg border px-2 py-3 text-xs sm:text-sm font-semibold text-center transition-colors leading-tight',
                isActive
                  ? styles.active
                  : 'border-neutral-300 bg-surface text-neutral-700 hover:border-neutral-400 hover:bg-neutral-50',
                readOnly ? 'cursor-default' : 'cursor-pointer',
              ].join(' ')}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {showRequired && (
        <p className="text-xs text-danger-500 font-medium">Selection required</p>
      )}
    </div>
  );
};

export default React.memo(TradingScoreWidgetRenderer);
